// Admin-only "undo completion" for a completed v2 work order.
//
// Opens with a dry-run preview from POST /undo-completion/ {dry_run: true} so
// the admin sees exactly what comes back off the books (finished units,
// allocation, materials returned to stock, order status) — or why the undo is
// refused (output already used by a parent WO / order line shipped) — before
// confirming. Confirming rewinds the WO to planned so it can be produced again.

import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, List, ListItem, ListItemText, TextField, Typography,
} from '@mui/material';
import { Undo } from '@mui/icons-material';
import { v2 } from '../../../../services/api';

function extractError(err) {
  const data = err?.response?.data;
  if (!data) return err?.message || 'Undo failed';
  if (typeof data === 'string') return data;
  return data.error || data.detail || 'Undo failed';
}

export default function AdminWOUndoCompletionDialogV2({
  open, workOrderId, woNumber, onClose, onUndone,
}) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [reason, setReason] = useState('');

  useEffect(() => {
    if (!open || workOrderId == null) return undefined;
    let cancelled = false;
    setPreview(null);
    setError(null);
    setReason('');
    setLoading(true);
    v2.workOrders.undoCompletion(workOrderId, { dry_run: true })
      .then((res) => { if (!cancelled) setPreview(res.data); })
      .catch((err) => { if (!cancelled) setError(extractError(err)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, workOrderId]);

  const handleClose = () => {
    if (submitting) return;
    onClose();
  };

  const handleConfirm = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const res = await v2.workOrders.undoCompletion(workOrderId, {
        reason: reason.trim(),
      });
      onUndone?.(res.data);
      onClose();
    } catch (err) {
      setError(extractError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const materials = preview?.materials_returned || [];

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth data-testid="undo-completion-dialog">
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Undo /> Undo completion — {woNumber}
      </DialogTitle>
      <DialogContent dividers>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={28} />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: preview ? 2 : 0 }} data-testid="undo-completion-error">
            {error}
          </Alert>
        )}

        {preview && (
          <>
            <Typography variant="body2" sx={{ mb: 1.5 }}>
              This puts the work order back to <strong>planned</strong> as if the
              production never happened. It can then be produced again.
            </Typography>
            <List dense disablePadding data-testid="undo-completion-preview">
              <ListItem disableGutters>
                <ListItemText
                  primary={`Remove ${preview.quantity_removed} × ${preview.output_item_code} from stock`}
                  secondary={
                    parseFloat(preview.allocation_removed) > 0
                      ? `${preview.allocation_removed} un-allocated from the customer order`
                      : null
                  }
                />
              </ListItem>
              {parseFloat(preview.scrap_removed) > 0 && (
                <ListItem disableGutters>
                  <ListItemText primary={`Clear ${preview.scrap_removed} recorded scrap`} />
                </ListItem>
              )}
              {materials.length > 0 && (
                <ListItem disableGutters sx={{ alignItems: 'flex-start' }}>
                  <ListItemText
                    primary="Return materials to stock"
                    secondary={materials.map((m) => `${m.item_code} × ${m.quantity}`).join(', ')}
                  />
                </ListItem>
              )}
              {preview.order_status_restored_to && (
                <ListItem disableGutters>
                  <ListItemText
                    primary={`Order status back to ${preview.order_status_restored_to.replace(/_/g, ' ')}`}
                  />
                </ListItem>
              )}
            </List>
            {(preview.warnings || []).map((w) => (
              <Alert key={w} severity="warning" sx={{ mt: 1 }}>{w}</Alert>
            ))}
            <TextField
              label="Reason (optional)"
              fullWidth
              size="small"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              sx={{ mt: 2 }}
              inputProps={{ 'data-testid': 'undo-completion-reason' }}
            />
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={submitting}>Cancel</Button>
        <Button
          variant="contained"
          color="error"
          startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <Undo />}
          disabled={!preview || loading || submitting}
          onClick={handleConfirm}
          data-testid="undo-completion-confirm"
        >
          {submitting ? 'Undoing…' : 'Undo completion'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
